<template>
  <ba-card
    class="stats-card"
    :class="{ 'is-collapsed': !expanded }"
  >
    <template slot="header">
      <div
        class="stats-header"
        :class="{ 'is-open': expanded }"
        @click="toggleExpanded"
      >
        <h2 class="title">
          {{ $t("page.dashboard.stats.title") }}
        </h2>
        <span class="stats-hint">
          {{ expanded ? $t("action.close") : $t("action.open") }}
        </span>
        <ab-icon
          class="stats-chevron"
          icon="desc"
        />
      </div>
    </template>

    <template>
      <div
        v-show="expanded"
        class="stats-body"
      >
        <!-- Filtre par période -->
        <div class="period-filter">
          <div class="period-controls">
            <div class="basic-date">
              <label>{{ $t("page.dashboard.stats.dateStart") }}</label>
              <datepicker
                v-model="dateStart"
                class="basic-datepicker position-left"
                format="dd/MM/yyyy"
                :monday-first="true"
                :language="fr"
              />
              <ab-icon
                icon="calendar"
                iconset="calendarSet"
              />
            </div>
            <div class="basic-date">
              <label>{{ $t("page.dashboard.stats.dateEnd") }}</label>
              <datepicker
                v-model="dateEnd"
                class="basic-datepicker position-left"
                format="dd/MM/yyyy"
                :monday-first="true"
                :language="fr"
              />
              <ab-icon
                icon="calendar"
                iconset="calendarSet"
              />
            </div>
            <div class="period-shortcuts">
              <button
                type="button"
                :class="['shortcut', { active: activePeriod === '7d' }]"
                @click="setPeriod('7d')"
              >
                {{ $t("page.dashboard.stats.period7d") }}
              </button>
              <button
                type="button"
                :class="['shortcut', { active: activePeriod === '30d' }]"
                @click="setPeriod('30d')"
              >
                {{ $t("page.dashboard.stats.period30d") }}
              </button>
              <button
                type="button"
                :class="['shortcut', { active: activePeriod === 'month' }]"
                @click="setPeriod('month')"
              >
                {{ $t("page.dashboard.stats.periodMonth") }}
              </button>
            </div>
          </div>
        </div>

        <!-- Sélecteurs : compagnie + vendeur (Admin / Manager) + type -->
        <div class="stats-selectors">
          <div
            v-if="isManager"
            class="selector"
          >
            <ba-select
              v-model="company"
              :label="$t('page.dashboard.stats.company')"
              :allow-empty="true"
              :empty-label="$t('page.dashboard.all')"
            >
              <option
                v-for="(c, index) in companies"
                :key="`stats-company${index}`"
                :value="c.id"
              >
                {{ c.name || c.label }}
              </option>
            </ba-select>
          </div>

          <div
            v-if="isManager"
            class="selector"
          >
            <ba-select
              v-model="userId"
              :label="$t('page.dashboard.stats.seller')"
              :allow-empty="true"
              :empty-label="$t('page.dashboard.all')"
            >
              <option
                v-for="(u, index) in sellers"
                :key="`stats-seller${index}`"
                :value="u.id"
              >
                {{ u.first_name }} {{ u.last_name }}
              </option>
            </ba-select>
          </div>

          <div class="selector">
            <ba-select
              v-model="requestTypeId"
              :label="$t('page.dashboard.stats.type')"
              :allow-empty="true"
              :empty-label="$t('page.dashboard.all')"
            >
              <option :value="1">
                {{ $t("page.dashboard.stats.motor") }}
              </option>
              <option :value="2">
                {{ $t("page.dashboard.stats.gearbox") }}
              </option>
            </ba-select>
          </div>
        </div>

        <!-- KPI synthèse (toujours visibles) -->
        <!-- <div class="kpi-grid">
          <div class="kpi">
            <span class="kpi-label">{{ $t("page.dashboard.stats.demande") }}</span>
            <span
              v-if="loading"
              class="kpi-loader"
            />
            <span
              v-else
              class="kpi-value"
            >{{ formatInt(total.demande) }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">{{ $t("page.dashboard.stats.devis") }}</span>
            <span
              v-if="loading"
              class="kpi-loader"
            />
            <template v-else>
              <span class="kpi-value">{{ formatInt(total.devis) }}</span>
              <span class="kpi-sub">{{ formatPercent(total.devis, total.demande) }}</span>
            </template>
          </div>
          <div class="kpi">
            <span class="kpi-label">{{ $t("page.dashboard.stats.vente") }}</span>
            <span
              v-if="loading"
              class="kpi-loader"
            />
            <template v-else>
              <span class="kpi-value">{{ formatInt(total.vente) }}</span>
              <span class="kpi-sub">{{ formatPercent(total.vente, total.demande_immat) }}</span>
            </template>
          </div>
          <div class="kpi">
            <span class="kpi-label">{{ $t("page.dashboard.stats.ca") }}</span>
            <span
              v-if="loading"
              class="kpi-loader"
            />
            <span
              v-else
              class="kpi-value"
            >{{ formatCurrency(total.ca_vente) }}</span>
          </div>
          <div class="kpi">
            <span class="kpi-label">{{ $t("page.dashboard.stats.repartitionMB") }}</span>
            <span
              v-if="loading"
              class="kpi-loader"
            />
            <span
              v-else
              class="kpi-value kpi-mb"
            >{{ repartitionMB }}</span>
          </div>
        </div> -->

        <!-- Détail : matrice Total / PRE / Reman / Negoce -->
        <!-- v-show="expanded" -->
        <div
          class="stats-detail"
        >
          <div
            v-if="loading"
            class="detail-loader-overlay"
          >
            <mo-loader :loading="loading" />
          </div>
          <ba-table :class="{ 'is-loading': loading }">
            <template slot="head">
              <tr>
                <th
                  rowspan="2"
                  class="left"
                />
                <th
                  v-for="col in columns"
                  :key="`h-${col.key}`"
                  colspan="2"
                  class="center"
                  :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                >
                  {{ col.label }}
                </th>
              </tr>
              <tr>
                <template v-for="col in columns">
                  <th
                    :key="`hn-${col.key}`"
                    class="center"
                    :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                  >
                    {{ $t("page.dashboard.stats.volume") }}
                  </th>
                  <th
                    :key="`he-${col.key}`"
                    class="center"
                    :class="{ 'col-total': col.isTotal }"
                  >
                    {{ $t("page.dashboard.stats.ca") }}
                  </th>
                </template>
              </tr>
            </template>

            <template slot="body">
              <tr>
                <td class="center">
                  {{ $t("page.dashboard.stats.demande") }}
                </td>
                <template v-for="col in columns">
                  <td
                    :key="`dem-n-${col.key}`"
                    class="center"
                    :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                  >
                    {{ col.isTotal ? formatInt(col.m.demande) : "" }}
                  </td>
                  <td
                    :key="`dem-e-${col.key}`"
                    class="center"
                    :class="{ 'col-total': col.isTotal }"
                  />
                </template>
              </tr>
              <tr>
                <td class="center">
                  {{ $t("page.dashboard.stats.devis") }}
                </td>
                <template v-for="col in columns">
                  <td
                    :key="`dev-n-${col.key}`"
                    class="center"
                    :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                  >
                    {{ formatInt(col.m.devis) }}
                  </td>
                  <td
                    :key="`dev-e-${col.key}`"
                    class="center"
                    :class="{ 'col-total': col.isTotal }"
                  >
                    {{ formatCurrency(col.m.ca_devis) }}
                  </td>
                </template>
              </tr>
              <tr>
                <td class="center">
                  {{ $t("page.dashboard.stats.pctDevis") }}
                </td>
                <template v-for="col in columns">
                  <td
                    :key="`pd-n-${col.key}`"
                    class="center"
                    :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                  >
                    {{ percentDevisCount(col) }}
                  </td>
                  <td
                    :key="`pd-e-${col.key}`"
                    class="center"
                    :class="{ 'col-total': col.isTotal }"
                  >
                    {{ percentDevisAmount(col) }}
                  </td>
                </template>
              </tr>
              <tr>
                <td class="center">
                  {{ $t("page.dashboard.stats.vente") }}
                </td>
                <template v-for="col in columns">
                  <td
                    :key="`ven-n-${col.key}`"
                    class="center"
                    :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                  >
                    {{ formatInt(col.m.vente) }}
                  </td>
                  <td
                    :key="`ven-e-${col.key}`"
                    class="center"
                    :class="{ 'col-total': col.isTotal }"
                  >
                    {{ formatCurrency(col.m.ca_vente) }}
                  </td>
                </template>
              </tr>
              <tr>
                <td class="center">
                  {{ $t("page.dashboard.stats.transfo") }}
                </td>
                <template v-for="col in columns">
                  <td
                    :key="`pt-n-${col.key}`"
                    class="center"
                    :class="{ 'col-sep': true, 'col-total': col.isTotal }"
                  >
                    {{ percentTransfoCount(col) }}
                  </td>
                  <td
                    :key="`pt-e-${col.key}`"
                    class="center"
                    :class="{ 'col-total': col.isTotal }"
                  >
                    {{ percentTransfoAmount(col) }}
                  </td>
                </template>
              </tr>
            </template>
          </ba-table>
        </div>

        <!-- <div class="stats-toggle">
          <button
            type="button"
            class="toggle-btn"
            @click="toggleExpanded"
          >
            {{ expanded ? $t("page.dashboard.stats.collapse") : $t("page.dashboard.stats.expand") }}
          </button>
        </div> -->
      </div>
    </template>
  </ba-card>
</template>

<script>
import { AbIcon, BaCard, BaSelect, BaTable } from "@ab/basic-components";
import { mapState } from "vuex";
import { fr } from "vuejs-datepicker/dist/locale";
import Datepicker from "vuejs-datepicker";
import MoLoader from "@/components/loader/mo-loader.vue";

export default {
  name: "DashboardStats",
  components: {
    AbIcon,
    BaCard,
    BaSelect,
    BaTable,
    Datepicker,
    MoLoader
  },
  data() {
    return {
      fr,
      loading: true,
      expanded: false,
      activePeriod: "30d",
      dateStart: null,
      dateEnd: null,
      company: "",
      userId: "",
      requestTypeId: ""
    };
  },
  computed: {
    ...mapState({ userRole: state => state.authentication.userRole }),
    ...mapState({ companies: state => state.companies.statsCompanies }),
    ...mapState({ sellers: state => state.requests.sellers }),
    ...mapState({ stats: state => state.requests.stats }),
    isManager() {
      return [ "Admin", "Manager" ].includes(this.userRole);
    },
    total() {
      return (this.stats && this.stats.total) || {};
    },
    repartitionMB() {
      return `${this.formatInt(this.total.nb_moteur)} M / ${this.formatInt(this.total.nb_boite)} B`;
    },
    columns() {
      const t = this.total;
      const origines = t.origines || {};
      const totalMetrics = {
        demande: t.demande,
        demande_immat: t.demande_immat,
        devis: t.devis,
        ca_devis: t.ca_devis,
        vente: t.vente,
        ca_vente: t.ca_vente
      };
      return [
        {
          key: "total", label: this.$t("page.dashboard.stats.total"), m: totalMetrics, isTotal: true
        },
        {
          key: "PRE", label: "PRE", m: origines.PRE || {}, isTotal: false
        },
        {
          key: "Reman", label: "Reman", m: origines.Reman || {}, isTotal: false
        },
        {
          key: "Negoce", label: "Negoce", m: origines.Negoce || {}, isTotal: false
        }
      ];
    }
  },
  watch: {
    dateStart() {
      this.activePeriod = "";
      this.fetchStats();
    },
    dateEnd() {
      this.activePeriod = "";
      this.fetchStats();
    },
    company() {
      this.userId = "";
      this.$store.dispatch("getStatsSellers", this.company || undefined);
      this.fetchStats();
    },
    userId() {
      this.fetchStats();
    },
    requestTypeId() {
      this.fetchStats();
    }
  },
  async mounted() {
    if (this.isManager) {
      await Promise.all([
        this.$store.dispatch("getStatsCompanies"),
        this.$store.dispatch("getStatsSellers")
      ]);
    }
    this.setPeriod("30d");
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded;
    },
    setPeriod(type) {
      this.activePeriod = type;
      const now = new Date();
      if (type === "7d") {
        const start = new Date();
        start.setDate(start.getDate() - 7);
        this.dateStart = start;
        this.dateEnd = now;
      } else if (type === "30d") {
        const start = new Date();
        start.setDate(start.getDate() - 30);
        this.dateStart = start;
        this.dateEnd = now;
      } else if (type === "month") {
        this.dateStart = new Date(now.getFullYear(), now.getMonth(), 1);
        this.dateEnd = now;
      }
      this.$nextTick(() => {
        this.activePeriod = type;
      });
    },
    async fetchStats() {
      const params = {
        companies: this.company || undefined,
        user_id: this.userId || undefined,
        date_min: this.dateStart ? this.dateStart.getTime() : undefined,
        date_max: this.dateEnd ? this.dateEnd.getTime() : undefined,
        request_type_id: this.requestTypeId || undefined
      };
      this.loading = true;
      try {
        await this.$store.dispatch("getRequestStats", params);
      } finally {
        this.loading = false;
      }
    },
    formatInt(value) {
      return Number(value || 0).toLocaleString("fr-FR");
    },
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} €`;
    },
    formatPercent(part, whole) {
      if (!whole) return "0,00 %";
      const pct = (Number(part || 0) / Number(whole)) * 100;
      return `${pct.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })} %`;
    },

    percentDevisCount(col) {
      return this.formatPercent(col.m.devis, this.total.demande);
    },
    percentDevisAmount(col) {
      return col.isTotal ? "" : this.formatPercent(col.m.ca_devis, this.total.ca_devis);
    },

    percentTransfoCount(col) {
      return this.formatPercent(col.m.vente, this.total.demande);
    },

    percentTransfoAmount(col) {
      return this.formatPercent(col.m.ca_vente, col.m.ca_devis);
    }
  }
};
</script>
<style scoped>
.stats-card {
  width: auto;
  margin: 0 2rem 2rem;
}

.stats-card ::v-deep .main {
  padding: 0;
}

.stats-card ::v-deep .header {
  transition: background-color 0.15s ease;
}

/* .stats-card ::v-deep .header:hover {
  background-color: #7b95b8;
} */

.stats-card.is-collapsed ::v-deep .header {
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
}

.stats-header {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.6rem;
  cursor: pointer;
  user-select: none;
}

.stats-hint {
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;
}

.stats-chevron {
  margin: 0;
  margin-right: 5px;
  transition: transform 0.25s ease;
}

.stats-header.is-open .stats-chevron {
  transform: rotate(180deg);
}

.stats-chevron ::v-deep path {
  fill: #ffffff;
}

.stats-header .title {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.stats-body {
  padding: 1.25rem;
}

.stats-toggle {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.toggle-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  transition: background 0.15s ease;
}

.toggle-btn:hover {
  background: #eff6ff;
}

.toggle-btn .caret {
  font-size: 10px;
}

.period-filter {
  margin-bottom: 1.5rem;
}

.period-label {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #6b7280;
  margin: 0 0 0.25rem;
}

.period-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1rem;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
}

.period-controls .basic-date {
  flex: 0 0 auto;
  width: 170px;
}

.period-shortcuts {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.shortcut {
  border: none;
  background: #e0edff;
  color: #2563eb;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  padding: 0.55rem 0.9rem;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.shortcut:hover {
  background: #c7ddff;
}

.shortcut.active {
  background: #2563eb;
  color: #fff;
}

.stats-selectors {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.selector {
  flex: 1 1 200px;
  min-width: 180px;
  max-width: 320px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 1rem;
}

.kpi {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.4rem;
  min-height: 92px;
}

.kpi-label {
  font-size: 11px;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.kpi-mb {
  font-size: 17px;
}

.kpi-loader {
  width: 22px;
  height: 22px;
  margin-top: 4px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: kpi-spin 0.7s linear infinite;
}

@keyframes kpi-spin {
  to {
    transform: rotate(360deg);
  }
}

.kpi-sub {
  font-size: 12px;
  color: #16a34a;
  font-weight: 600;
}

.stats-detail {
  margin-top: 1.5rem;
  overflow-x: auto;
  position: relative;
}

.detail-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-loading {
  visibility: hidden;
}

.stats-detail .col-sep {
  border-left: 1px solid #cbd5e1;
}

.stats-detail .ba-table tbody tr td:first-child {
  padding: 20px 10px;
}

.stats-detail .ba-table thead tr:last-child th:first-child,
.stats-detail .ba-table thead tr:last-child th:last-child {
  border-radius: 0;
}

.stats-detail .ba-table .col-total {
  font-weight: 700;
}

.stats-detail .ba-table thead tr th.col-total {
  background-color: #4f6f9b;
  color: #ffffff;
}

.stats-detail .ba-table tbody tr td.col-total {
  background-color: #ffffff;
  color: #1f3a63;
}

.stats-detail .ba-table tbody tr:nth-child(2n) td.col-total {
  background-color: #d2e5ff;
}

.seller-detail {
  margin-top: 1.75rem;
}

.sub-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 0.75rem;
}

@media (max-width: 900px) {
  .stats-card {
    margin: 0 1rem 1.5rem;
  }

  .stats-body {
    padding: 1rem;
  }
}

@media (max-width: 600px) {
  .stats-card {
    margin: 0 0.5rem 1rem;
  }

  .stats-body {
    padding: 0.85rem;
  }

  .stats-header .title {
    font-size: 16px;
  }

  .kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
  }

  .kpi-value {
    font-size: 20px;
  }

  .selector {
    flex-basis: 100%;
    max-width: none;
  }

  .period-controls .basic-date {
    flex: 1 1 100%;
    width: auto;
    min-width: 0;
  }
}
</style>
